// ---------------------------------------------------------------------------
// Collections — mock usage graph (scale build). No backend.
//
// Only `usageShots()` survives from the original mock catalog: it fabricates
// the "used in" shot rows the inspector shows for sample collections. The
// catalog is a deterministic list so the rows are stable across reloads.
// ---------------------------------------------------------------------------

type Meta = {
  name: string
  film: string
  usedIn: number
}

// The sample catalog: name, home film, and how many shots reference it.
const META: Meta[] = [
  // People
  { name: 'Sarah', film: 'Coffee Shop', usedIn: 14 },
  { name: 'Detective Cole', film: 'Night Shift', usedIn: 11 },
  { name: 'Elena', film: 'Coffee Shop', usedIn: 4 },
  { name: 'Maya Chen', film: 'The Letter', usedIn: 3 },
  { name: 'Marcus', film: 'Night Shift', usedIn: 2 },
  { name: 'Priya', film: 'The Letter', usedIn: 5 },
  { name: 'Jamal', film: 'Night Shift', usedIn: 3 },
  { name: 'Grandma Rose', film: 'The Letter', usedIn: 2 },
  { name: 'Grandpa Walt', film: 'The Letter', usedIn: 2 },
  { name: 'Barista', film: 'Coffee Shop', usedIn: 1 },
  // Wardrobe
  { name: 'Red dress', film: 'Coffee Shop', usedIn: 6 },
  { name: 'Evening gown', film: 'The Letter', usedIn: 2 },
  { name: 'Trench coat', film: 'Night Shift', usedIn: 8 },
  { name: 'Detective suit', film: 'Night Shift', usedIn: 5 },
  { name: 'Barista apron', film: 'Coffee Shop', usedIn: 4 },
  // Props
  { name: 'Coffee mug', film: 'Coffee Shop', usedIn: 9 },
  { name: 'Espresso cup', film: 'Coffee Shop', usedIn: 3 },
  { name: 'Pocket watch', film: 'The Letter', usedIn: 2 },
  { name: 'Wristwatch', film: 'Night Shift', usedIn: 1 },
  { name: 'Gift box', film: 'The Letter', usedIn: 1 },
  // Places
  { name: 'Café interior', film: 'Coffee Shop', usedIn: 18 },
  { name: 'Forest', film: 'The Hunt', usedIn: 6 },
  { name: 'Neon alley', film: 'Night Shift', usedIn: 5 },
  { name: 'Downtown', film: 'Night Shift', usedIn: 4 },
  { name: 'Diner', film: 'Night Shift', usedIn: 7 },
  { name: 'Rooftop', film: 'Night Shift', usedIn: 2 },
  { name: 'Pine woods', film: 'The Hunt', usedIn: 2 },
  { name: 'Desert highway', film: 'The Hunt', usedIn: 3 },
  { name: 'Mountain pass', film: 'The Hunt', usedIn: 2 },
  // Vehicles
  { name: 'Muscle car', film: 'Night Shift', usedIn: 4 },
  { name: 'Sports car', film: 'Night Shift', usedIn: 3 },
  { name: 'Sedan', film: 'The Letter', usedIn: 2 },
]

// Deterministic PRNG (mulberry32) so the mock is stable across reloads.
function mulberry32(seed: number) {
  return function () {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Stable per-name hash so different collections get different shot rows. */
function nameOffset(name: string): number {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return h
}

// --- Usage graph -------------------------------------------------------------
//
// Board placements are LINKED references into the library, not copies — so a
// collection knows every shot it appears in, across projects. This reverse
// index is what makes the library the production's memory (and what makes
// leaving expensive): approve a new hero and the linked shots follow.

export type UsageShot = { shot: string; project: string; i: number }

const SCENES = ['Rooftop dusk', 'Café window', 'Neon chase', 'Kitchen argument', 'The letter', 'Parking garage', 'Morning walk', 'Stakeout', 'Cold open', 'Finale'] // prettier-ignore

/** Shots referencing this collection, home film first (capped at 8 for the popover). */
export function usageShots(name: string): UsageShot[] {
  const m = META.find(x => x.name === name)
  if (!m) return []
  const rng = mulberry32(nameOffset(name) + 13)
  const others = [...new Set(META.map(x => x.film))].filter(f => f !== m.film)
  const out: UsageShot[] = []
  for (let i = 0; i < Math.min(m.usedIn, 8); i++) {
    const cross = m.usedIn >= 5 && rng() < 0.3
    const project = cross ? others[Math.floor(rng() * others.length)] : m.film
    out.push({ shot: `SC ${1 + Math.floor(rng() * 28)} · ${SCENES[Math.floor(rng() * SCENES.length)]}`, project, i })
  }
  return out.sort((a, b) => (a.project === m.film ? 0 : 1) - (b.project === m.film ? 0 : 1))
}
